import React from 'react';
import {useField, useFormikContext} from 'formik';
import {useHistory} from 'react-router-dom';

import {Box, Button, Grid, TextField} from '@material-ui/core';
import {GridContainer} from '@crema';
import AppCard from '@crema/core/AppCard';

import {FormLoadCreateGeneralProps} from './form-load-create-general.types';
import {loadEquipment} from 'shared/constants/AppConst';

import {
  FormDateTimePicker,
  FormCheckbox,
  SeparatorLine,
  FormSubtitle,
  FormMultipleSelectPicker,
  FormSelectPicker,
} from '../../atoms';
import {Fonts} from 'shared/constants/AppEnums';
import {Autocomplete} from '@mui/material';

function getTimeZoneAbbreviation(date: Date, timeZone?: string) {
  if (!timeZone) return '';
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone,
      timeZoneName: 'short',
    }).formatToParts(date);
    const tzPart = parts.find((part) => part.type === 'timeZoneName');
    const tzAbbr = tzPart ? tzPart.value : '';

    // Get UTC offset
    const utcOffset =
      new Intl.DateTimeFormat('en-US', {
        timeZone,
        timeZoneName: 'longOffset',
      })
        .formatToParts(date)
        .find((part) => part.type === 'timeZoneName')?.value || '';

    return `${tzAbbr} (${utcOffset})`;
  } catch (e) {
    return '';
  }
}

export const FormLoadCreateGeneral: React.FC<FormLoadCreateGeneralProps> = (
  props,
) => {
  const history = useHistory();
  const {values} = useFormikContext<any>();
  const now = new Date();
  const pickupTzAbbr = values.pickupTimezone
    ? getTimeZoneAbbreviation(now, values.pickupTimezone)
    : '';
  const dropoffTzAbbr = values.dropoffTimezone
    ? getTimeZoneAbbreviation(now, values.dropoffTimezone)
    : '';
  const expirationTzAbbr = values.expirationTimezone
    ? getTimeZoneAbbreviation(now, values.expirationTimezone)
    : '';

  const [fieldCheckboxPickup] = useField('addPickupDatetimeRange');
  const [fieldCheckboxDropoff] = useField('addDropoffDatetimeRange');
  const [fieldPickup, metaPickup, helpersPickup] = useField('pickupTimezone');
  const [fieldDropoff, metaDropoff, helpersDropoff] =
    useField('dropoffTimezone');
  const [fieldExpiration, metaExpiration, helpersExpiration] =
    useField('expirationTimezone');

  const handleBatchUploadClicked = () => {
    history.push('/loads/upload');
  };

  const loadId = history.location.pathname.split('/')[3] || '';

  return (
    <>
      <Box
        component='h1'
        color='text.primary'
        fontWeight={Fonts.BOLD}
        fontSize={16}
        style={{
          marginBottom: '20px',
          display: 'flex',
          justifyContent: 'space-between',
        }}
        mb={1}>
        {props.isUpdating && `Updating Load ${loadId}`} General info
        <Button
          variant='contained'
          color='default'
          onClick={handleBatchUploadClicked}>
          Batch upload
        </Button>
      </Box>

      <AppCard style={{padding: 20}} mb={5}>
        <Box flex={1} flexDirection='column'>
          <FormSubtitle label='Pickup and dropoff dates and times' />

          <Box style={{width: '100%'}}>
            <GridContainer>
              {/* Pickup dates */}
              <Grid item xs={12} md={6}>
                <Box mb={{xs: 0, xl: 4}}>
                  <FormDateTimePicker
                    name='pickupDatetimeStart'
                    label='Pickup date and time'
                  />

                  <Box mt={2}>
                    <Autocomplete
                      value={
                        fieldPickup.value
                          ? {id: fieldPickup.value, name: fieldPickup.value}
                          : null
                      }
                      onChange={(event, newValue) => {
                        helpersPickup.setValue(newValue?.id || '');
                      }}
                      options={Intl.supportedValuesOf('timeZone')
                        .filter((tz) => tz.startsWith('America/'))
                        .map((tz) => ({
                          id: tz,
                          name: tz,
                        }))}
                      getOptionLabel={(option) => option.name}
                      isOptionEqualToValue={(option, value) =>
                        option.id === value.id
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label='Timezone'
                          name='pickupTimezone'
                          error={
                            metaPickup.touched && Boolean(metaPickup.error)
                          }
                          helperText={metaPickup.touched && metaPickup.error}
                        />
                      )}
                    />
                    {values.pickupTimezone && (
                      <Box
                        style={{
                          color: '#888',
                          marginLeft: 8,
                          paddingTop: 8,
                          fontSize: 12,
                        }}>
                        {pickupTzAbbr}
                      </Box>
                    )}
                  </Box>

                  <FormCheckbox
                    name='addPickupDatetimeRange'
                    label='Add latest pickup time'
                  />
                  {fieldCheckboxPickup.value && (
                    <Box mt={2} style={{width: '100%'}}>
                      <FormDateTimePicker
                        name='pickupDatetimeEnd'
                        label='Latest pickup date and time'
                      />
                    </Box>
                  )}
                </Box>
              </Grid>

              {/* Dropoff dates */}
              <Grid item xs={12} md={6}>
                <Box mb={{xs: 3, xl: 4}}>
                  <FormDateTimePicker
                    name='dropoffDatetimeStart'
                    label='Dropoff date and time'
                  />

                  <Box mt={2}>
                    <Autocomplete
                      value={
                        fieldDropoff.value
                          ? {id: fieldDropoff.value, name: fieldDropoff.value}
                          : null
                      }
                      onChange={(event, newValue) => {
                        helpersDropoff.setValue(newValue?.id || '');
                      }}
                      options={Intl.supportedValuesOf('timeZone')
                        .filter((tz) => tz.startsWith('America/'))
                        .map((tz) => ({
                          id: tz,
                          name: tz,
                        }))}
                      getOptionLabel={(option) => option.name}
                      isOptionEqualToValue={(option, value) =>
                        option.id === value.id
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label='Timezone'
                          name='dropoffTimezone'
                          error={
                            metaDropoff.touched && Boolean(metaDropoff.error)
                          }
                          helperText={metaDropoff.touched && metaDropoff.error}
                        />
                      )}
                    />
                    {values.dropoffTimezone && (
                      <Box
                        style={{
                          color: '#888',
                          marginLeft: 8,
                          paddingTop: 8,
                          fontSize: 12,
                        }}>
                        {dropoffTzAbbr}
                      </Box>
                    )}
                  </Box>

                  <FormCheckbox
                    name='addDropoffDatetimeRange'
                    label='Add latest dropoff time'
                  />
                  {fieldCheckboxDropoff.value && (
                    <Box mt={2} style={{width: '100%'}}>
                      <FormDateTimePicker
                        name='dropoffDatetimeEnd'
                        label='Latest dropoff date and time'
                      />
                    </Box>
                  )}
                </Box>
              </Grid>
            </GridContainer>
          </Box>

          {/* Separator */}
          <SeparatorLine />

          <Box style={{width: '100%'}} mt={6}>
            <GridContainer>
              <Grid item xs={12} md={6}>
                <FormDateTimePicker
                  name='offerExpirationDatetime'
                  label='Offer expiration date and time'
                />
                <Box mt={2}>
                  <Autocomplete
                    value={
                      fieldExpiration.value
                        ? {
                            id: fieldExpiration.value,
                            name: fieldExpiration.value,
                          }
                        : null
                    }
                    onChange={(event, newValue) => {
                      helpersExpiration.setValue(newValue?.id || '');
                    }}
                    options={Intl.supportedValuesOf('timeZone')
                      .filter((tz) => tz.startsWith('America/'))
                      .map((tz) => ({
                        id: tz,
                        name: tz,
                      }))}
                    getOptionLabel={(option) => option.name}
                    isOptionEqualToValue={(option, value) =>
                      option.id === value.id
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label='Timezone'
                        name='expirationTimezone'
                        error={
                          metaExpiration.touched &&
                          Boolean(metaExpiration.error)
                        }
                        helperText={
                          metaExpiration.touched && metaExpiration.error
                        }
                      />
                    )}
                  />
                  {values.expirationTimezone && (
                    <Box
                      style={{
                        color: '#888',
                        marginLeft: 8,
                        paddingTop: 8,
                        fontSize: 12,
                      }}>
                      {expirationTzAbbr}
                    </Box>
                  )}
                </Box>
              </Grid>
            </GridContainer>
          </Box>

          <Box style={{width: '100%'}} mt={6}>
            <Grid item xs={12} md={6}>
              <Box mb={{xs: 4, xl: 6}}>
                <FormMultipleSelectPicker
                  name='loadEquipmentSelect'
                  items={loadEquipment}
                  label='Select Load Equipment *'
                />
              </Box>
            </Grid>
          </Box>
        </Box>
      </AppCard>
    </>
  );
};
